import { useEffect, useState } from "react";
import {
  Button,
  Container,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useStepperContext } from "app/contexts/stepper";
import { useInscripcionContext } from "../../inscripcion-context";
import { useResourcesContext } from "app/contexts/resources";
import { filterSpaces, getMaxDate } from "app/utils";
import { rtnRegex } from "app/utils/regexps";
import {
  DenominacionSocialSelect,
  DepartamentoSelect,
  MunicipioAutocomplete,
  RepresentacionGeograficaSelect,
} from "app/components";

const initialDepto = {
  departamento_id: 0,
  ciudades: [],
};

const initialCity = {
  ciudad_id: 0,
  ciudad_matricula_requerida: false,
  ciudad_inscripcion_requerida: true,
  ciudad_tomo_requerido: true,
};

export function InformacionGeneralForm() {
  const {
    denominacionesSociales,
    representacionesGeograficas,
    decretosLegislativos,
    departamentosDeHonduras,
    getDepartamentoByCiudadId,
    getCiudad,
    getDepartamento,
    camarasDeComercio,
  } = useResourcesContext();

  const stepper = useStepperContext();

  const { empresa, setEmpresa } = useInscripcionContext();
  const [ciudad, setCiudad] = useState(initialCity);

  useEffect(() => {
    // si existe el id de la ciudad que seleccione el departamento y después la ciudad
    if (!!empresa.registro_mercantil_ciudad_id) {
      const ciudad = getCiudad(empresa.registro_mercantil_ciudad_id);

      setCiudad(ciudad);
    }
  }, [
    empresa.registro_mercantil_ciudad_id,
    getDepartamentoByCiudadId,
    getCiudad,
  ]);

  function handleSubmit(e) {
    e.preventDefault();

    stepper.next();
  }

  return (
    <Container className="animate__animated animate__fadeIn">
      <Paper
        component="form"
        onSubmit={handleSubmit}
        sx={{ padding: 2 }}
        variant="outlined"
      >
        <Grid container spacing={2}>
          {/* Datos Generales */}
          <Grid item container xs={12} md={12} spacing={1}>
            <Grid item xs={12}>
              <Typography variant="overline">
                <strong>Datos Generales</strong>
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <DenominacionSocialSelect
                value={empresa.denominacion_social_id}
                onChange={(value) =>
                  setEmpresa((empresa) => ({
                    ...empresa,
                    denominacion_social_id: value,
                  }))
                }
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                type="text"
                label="Nombre comercial"
                value={empresa.empresa_nombre_comercial}
                onChange={(e) =>
                  setEmpresa((empresa) => ({
                    ...empresa,
                    empresa_nombre_comercial: filterSpaces({
                      value: e.target.value,
                      regex: /\s{2,}/,
                    }),
                  }))
                }
                size="small"
                fullWidth
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                type="text"
                label="RTN de la empresa, propietario o representante legal"
                value={empresa.empresa_rtn}
                onChange={(e) =>
                  setEmpresa((empresa) => ({
                    ...empresa,
                    empresa_rtn: e.target.value.trim(),
                  }))
                }
                inputProps={{
                  pattern: rtnRegex.pattern,
                  title: rtnRegex.title,
                }}
                size="small"
                fullWidth
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <RepresentacionGeograficaSelect
                value={empresa.representacion_geografica_id || ""}
                onChange={(value) =>
                  setEmpresa((empresa) => ({
                    ...empresa,
                    representacion_geografica_id: value,
                  }))
                }
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                type="number"
                label="Cantidad de empleados"
                value={empresa.empresa_cantidad_de_empleados || ""}
                onChange={(e) =>
                  setEmpresa((empresa) => ({
                    ...empresa,
                    empresa_cantidad_de_empleados: e.target.valueAsNumber,
                  }))
                }
                inputProps={{ min: 1 }}
                size="small"
                fullWidth
                required
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl size="small" fullWidth required>
                <InputLabel id="camara-de-comercio-label">
                  ¿La empresa está registrada en otra Cámara de Comercio?
                </InputLabel>

                <Select
                  label="¿La empresa está registrada en otra Cámara de Comercio?"
                  labelId="camara-de-comercio-label"
                  value={empresa.camara_de_comercio_id || ""}
                  onChange={(e) =>
                    setEmpresa((empresa) => ({
                      ...empresa,
                      camara_de_comercio_id: e.target.value,
                    }))
                  }
                >
                  {camarasDeComercio.results.map((camara) => (
                    <MenuItem
                      key={camara.camara_de_comercio_id}
                      value={camara.camara_de_comercio_id}
                    >
                      {camara.camara_de_comercio_nombre}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl size="small" fullWidth required>
                <InputLabel id="decreto-legislativo-label">
                  ¿La empresa está adherida a algún Decreto Legislativo?
                </InputLabel>

                <Select
                  label="¿La empresa está adherida a algún Decreto Legislativo?"
                  labelId="decreto-legislativo-label"
                  value={empresa.decreto_legislativo_id || ""}
                  onChange={(e) =>
                    setEmpresa((empresa) => ({
                      ...empresa,
                      decreto_legislativo_id: e.target.value,
                    }))
                  }
                >
                  {decretosLegislativos.results.map((decreto) => (
                    <MenuItem
                      key={decreto.decreto_legislativo_id}
                      value={decreto.decreto_legislativo_id}
                    >
                      {`${decreto.decreto_legislativo_numero} ${
                        decreto.decreto_legislativo_numero ? " - " : ""
                      } ${decreto.decreto_legislativo_nombre}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Registro Mercantil */}
          <Grid item container xs={12} md={12} spacing={1}>
            <Grid item xs={12}>
              <Typography variant="overline">
                <strong>Registro Mercantil</strong>
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <MunicipioAutocomplete
                value={empresa.registro_mercantil_ciudad_id}
                onChange={(value) => {
                  setEmpresa((empresa) => ({
                    ...empresa,
                    registro_mercantil_ciudad_id: value,
                  }));
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                type="date"
                label="Fecha"
                value={empresa.registro_mercantil_fecha}
                onChange={(e) =>
                  setEmpresa((empresa) => ({
                    ...empresa,
                    registro_mercantil_fecha: e.target.value,
                  }))
                }
                size="small"
                inputProps={{ min: "1900-01-01", max: getMaxDate() }}
                InputLabelProps={{
                  shrink: true,
                }}
                fullWidth
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                type="text"
                label={`Matricula (${
                  !!ciudad.ciudad_matricula_requerida ? "requerida" : "opcional"
                })`}
                value={empresa.registro_mercantil_matricula}
                onChange={(e) =>
                  setEmpresa((empresa) => ({
                    ...empresa,
                    registro_mercantil_matricula: e.target.value,
                  }))
                }
                size="small"
                fullWidth
                required={!!ciudad.ciudad_matricula_requerida}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                type="text"
                label={`No. de inscripción (${
                  !!ciudad.ciudad_inscripcion_requerida
                    ? "requerida"
                    : "opcional"
                })`}
                value={empresa.registro_mercantil_inscripcion}
                onChange={(e) =>
                  setEmpresa((empresa) => ({
                    ...empresa,
                    registro_mercantil_inscripcion: e.target.value.trim(),
                  }))
                }
                size="small"
                fullWidth
                required={!!ciudad.ciudad_inscripcion_requerida}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                type="text"
                label={`No. de tomo (${
                  !!ciudad.ciudad_tomo_requerido ? "requerido" : "opcional"
                })`}
                value={empresa.registro_mercantil_tomo}
                onChange={(e) =>
                  setEmpresa((empresa) => ({
                    ...empresa,
                    registro_mercantil_tomo: e.target.value.trim(),
                  }))
                }
                size="small"
                fullWidth
                required={!!ciudad.ciudad_tomo_requerido}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                type="number"
                label="Capital autorizado"
                value={empresa.registro_mercantil_capital_autorizado}
                onChange={(e) =>
                  setEmpresa((empresa) => ({
                    ...empresa,
                    registro_mercantil_capital_autorizado: e.target.value
                      ? e.target.valueAsNumber
                      : 0,
                  }))
                }
                size="small"
                inputProps={{ min: 0 }}
                fullWidth
                required
              />
            </Grid>
          </Grid>

          {/* Buttons */}
          <Grid item xs={12}>
            <Stack
              direction="row"
              justifyContent="end"
              spacing={1}
              alignItems="center"
            >
              {!stepper.isFirstStep && (
                <Button variant="outlined" size="small" onClick={stepper.back}>
                  Atrás
                </Button>
              )}

              <Button type="submit" variant="contained" size="small">
                Siguiente
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
}
